import React, { lazy, useState } from "react";
import FAQ from "../../pages/Faq";
import { useLocation, useNavigate } from "react-router-dom";

import { Helmet } from "react-helmet-async";
import {
  FaUserTie,
  FaBriefcase,
  FaRegFileLines,
  FaUserCheck,
  FaCoins,
  FaArrowsRotate,
  FaFileSignature,
  FaPenNib,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaCaretRight,
  FaBuilding,
  FaIdCard,
  FaClock,
  FaListCheck,
  FaThumbsUp,
} from "react-icons/fa6";

import {
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiMessageCircle,
  FiUsers,
  FiBriefcase,
  FiAward,
  FiSend,
} from "react-icons/fi";
import { Check } from "lucide-react";
import PromotionLetterGeneratorForm from "./PromotionLetterGeneratorForm";
import {
  FaBalanceScale,
  FaCalendarAlt,
  FaCertificate,
  FaFileAlt,
  FaMoneyBillWave,
  FaTasks,
} from "react-icons/fa";
const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description:
      "Add the company name, address and authorised signatory information",
  },
  {
    number: 2,
    title: "Add Employee Information",
    description:
      "Enter the employee’s name, ID, current designation and department",
  },
  {
    number: 3,
    title: "Provide Promotion Details",
    description:
      "Add the new designation, grade, department, reporting manager and effective date",
  },
  {
    number: 4,
    title: "Enter Revised Salary",
    description:
      "Provide the revised CTC, monthly salary, incentives and benefits",
  },
  {
    number: 5,
    title: "Add New Responsibilities",
    description:
      "Mention the key duties, authority and performance expectations associated with the promoted role",
  },
  {
    number: 6,
    title: "Generate the Letter",
    description:
      "The system will insert the information into the standard Promotion Letter Format",
  },
  {
    number: 7,
    title: "Review and Issue",
    description:
      "Verify the designation, salary, dates and responsibilities before signing or sharing the document",
  },
];
const features = [
  <>
    <strong>Startups & SMEs:</strong> Quickly prepare experience certificates
    when employees leave the organisation without spending time creating
    documents manually.
  </>,
  <>
    <strong>HR & Admin Teams:</strong> Create consistent experience letters for
    employees without repeatedly editing and formatting documents.
  </>,
  <>
    <strong>Recruitment Agencies:</strong> Prepare experience letters for
    contract, temporary, and placed employees quickly and professionally.
  </>,
  <>
    <strong>Consultants & Professional Firms:</strong> Generate employment
    documents for consultants, trainees, and project staff with a consistent
    professional format.
  </>,
  <>
    <strong>Schools & Educational Institutions:</strong> Issue experience
    certificates for teachers, administrators, and support employees when formal
    employment records are required.
  </>,
  <>
    <strong>Employees & Employers:</strong> Create a professional experience
    letter when a formal record of previous employment is required for future
    jobs, verification, or documentation.
  </>,
];
const Faqs1 = [
  {
    title: "What Should a Promotion Letter Include?",
    content:
      "A promotion letter should clearly state the employee’s new designation, effective date, updated responsibilities, reporting structure, and any changes to compensation or benefits.",
  },
  {
    title: "Can I Create a Promotion Letter Online for Free?",
    content:
      "Yes. You can create a professional promotion letter online by entering the employee and promotion details, then generate and download the completed document as a PDF.",
  },
  {
    title: "Can I Use a Promotion Letter Template?",
    content:
      "Yes. A promotion letter template provides a structured format for communicating the employee’s new role and terms. An online generator makes it easier to create the final document without editing a template manually.",
  },
  {
    title: "Should a Promotion Letter Mention Salary Changes?",
    content:
      "If the promotion includes a salary revision, the updated compensation can be included in the letter. If there is no immediate salary change, the letter can focus on the new role, responsibilities, and effective date.",
  },
  {
    title: "Who Can Create a Promotion Letter?",
    content:
      "HR teams, business owners, managers, and authorised administrators can create promotion letters when an employee moves to a new role, designation, grade, or level within the organisation.",
  },
  {
    title: "Can I Download the Promotion Letter as a PDF?",
    content:
      "Yes. After entering the required details, you can generate the promotion letter and download the finished document as a PDF for sharing with the employee or maintaining in HR records.",
  },
];

const schema = [
  // =========================================================
  // SCHEMA 1: WebApplication
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Waggex Free Promotion Letter Generator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Free online promotion letter generator. Enter company details, employee information, promotion details, revised designation, salary changes, and responsibilities to create and download a professional promotion letter PDF instantly.",
    url: "https://www.waggex.com/hr-documents/promotion-letter-generator",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    featureList: [
      "Free promotion letter generator India",
      "Online promotion letter format India",
      "Promotion letter PDF download",
      "Employee promotion letter template India",
      "Professional promotion letter maker India",
    ],
  },

  // =========================================================
  // SCHEMA 2: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is a promotion letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A promotion letter is an official document issued by an employer to confirm an employee's promotion to a higher role, designation, grade, or position. It usually includes the new designation, effective date, responsibilities, reporting structure, and applicable salary or compensation changes.",
        },
      },
      {
        "@type": "Question",
        name: "Can I create a promotion letter online for free?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. You can create a professional promotion letter online by entering the required company, employee, promotion, salary, and job details. Once completed, the promotion letter can be generated and downloaded as a PDF.",
        },
      },
      {
        "@type": "Question",
        name: "What details should be included in a promotion letter?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A promotion letter should generally include the employee's name, current designation, new designation, department, effective promotion date, revised salary or compensation if applicable, new responsibilities, reporting details, and other relevant terms and conditions of the promotion.",
        },
      },
      {
        "@type": "Question",
        name: "Should a promotion letter include salary details?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "If the promotion includes a salary revision, the updated salary, compensation, or applicable benefits can be mentioned in the promotion letter. If there is no salary change, the letter can focus on the employee's new designation, responsibilities, and effective date.",
        },
      },
      {
        "@type": "Question",
        name: "Can I download the promotion letter as a PDF?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. After entering the required details, you can generate the promotion letter and download the completed document as a professional PDF for sharing with the employee or maintaining in HR records.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 3: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];

const promotionLetterContent = [
  {
    icon: FaCertificate,
    title: "Promotion Confirmation",
    description: "A formal statement confirming the employee’s promotion.",
  },
  {
    icon: FaBalanceScale,
    title: "Current and New Designation",
    description: "The employee’s existing role and promoted position.",
  },
  {
    icon: FaCalendarAlt,
    title: "Effective Date",
    description:
      "The date from which the new designation and responsibilities will apply.",
  },
  {
    icon: FaBriefcase,
    title: "Revised Reporting Structure",
    description:
      "The employee’s new reporting manager, department or work location.",
  },
  {
    icon: FaMoneyBillWave,
    title: "Revised Salary and Benefits",
    description:
      "The updated annual CTC, monthly salary, incentives and additional benefits.",
  },
  {
    icon: FaTasks,
    title: "New Responsibilities",
    description:
      "The employee’s key duties, leadership responsibilities and performance expectations.",
  },
  {
    icon: FaFileAlt,
    title: "Recognition Statement",
    description:
      "A professional message acknowledging the employee’s contribution and performance.",
  },
  {
    icon: FaUserCheck,
    title: "Employee Acceptance",
    description:
      "An optional section where the employee confirms acceptance of the revised employment terms.",
  },
];
const experienceLetterPoints = [
  "Current and New Designation",
  "Promotion Effective Date",
  "Department and Work Location",
  "Reporting and Responsibilities",
  "Salary and Performance Expectations",
  "Employee Acknowledgement",
];
const companystepsFeatures = [
  {
    id: 1,
    icon: <FiClock />,
    title: "Save HR Time",
    desc: "Generate a promotion letter quickly without drafting every document manually or repeatedly entering employee details",
  },
  {
    id: 2,
    icon: <FiBriefcase />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent promotion letter format across departments, positions, and employee levels",
  },
  {
    id: 3,
    icon: <FiAlertTriangle />,
    title: "Reduce Salary and Designation Errors",
    desc: "Use structured fields to help prevent incorrect job titles, compensation figures, and promotion effective dates",
  },
  {
    id: 4,
    icon: <FiAward />,
    title: "Recognise Employee Performance",
    desc: "Formally acknowledge an employee's contribution, growth, achievements, and career progress through a professional promotion letter",
  },
  {
    id: 5,
    icon: <FiMessageCircle />,
    title: "Communicate New Expectations",
    desc: "Clearly explain the employee's revised duties, authority, responsibilities, and reporting structure after promotion",
  },
  {
    id: 6,
    icon: <FiFileText />,
    title: "Maintain Accurate HR Records",
    desc: "Store the promotion, designation, salary changes, and effective date in the employee's official HR records",
  },
];

const allexperiencePoints = [
  "Current designation",
  "Employment duration",
  "Achievements",
  "Additional responsibilities",
  "Skills and contribution",
  "Requested position",
];

const experiencePoints = [
  "New designation",
  "Effective date",
  "Revised salary",
  "Reporting structure",
  "New responsibilities",
];
export default function PromotionLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Free Promotion Letter Generator Online - PDF Download</title>
        <meta
          name="description"
          content="Create a professional promotion letter online with Waggex. Add designation, salary, and responsibilities, then generate and download a PDF instantly"
        />
        <meta
          name="keywords"
          content="promotion letter generator online,promotion letter format India,free promotion letter maker online India,job promotion letter template India,promotion letter PDF generator India,employee promotion letter format India,promotion letter Word format India,promotion letter sample India,promotion letter for employee India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/promotion-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-5 py-7 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Free Promotion Letter Generator Online
            </h1>

            <p className="mx-auto mt-8 max-w-5xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Create your professional employee promotion letter online with
              updated designation, responsibilities, reporting details, and
              compensation. Generate it in minutes and download it as PDF
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <PromotionLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS A PROMOTION LETTER?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      Create & Download Your Promotion Letter
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      Enter the employee’s new designation, effective date,
                      responsibilities, and compensation details. Create a
                      professional promotion letter online and download the
                      completed PDF in minutes
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {experienceLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < experienceLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  WHAT DOES THE PROMOTION LETTER TEMPLATE INCLUDE?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Our Tool Includes Everything Needed In An Employee Promotion
                  Document
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Add the employee’s new role, promotion date, responsibilities,
                  reporting details, and compensation changes. The tool
                  organizes these details into a clear, professional promotion
                  letter.
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {promotionLetterContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE A PROMOTION LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Generate Promotion the Letter in Simple Steps
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 7).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 2 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-8 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-8">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}

                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      UNDERSTAND THE DIFFERENCE
                    </span>
                  </div>

                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    Promotion Request vs Promotion Letter: Know the Difference
                  </h2>

                  {/* Intro */}
                  <p className="mt-7 max-w-2xl text-sm leading-8 text-[#10324F]">
                    A promotion request letter is written by an employee to ask
                    for a higher role or salary, while a promotion letter is
                    issued by the employer after the decision is made. The two
                    documents serve different purposes
                  </p>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Promotion Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {experiencePoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FiFileText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Promotion Request Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-73 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-4 py-6 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {allexperiencePoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHY USE AN ONLINE PROMOTION LETTER FORMAT?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
                  Create Professional Promotion Documents Faster
                </h2>
                {/* <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Send HR announcements, payroll updates, policy changes,
                  holiday notices, attendance reminders, event updates, and
                  important company communications from one platform
                </p> */}
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          {/* 
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
            
              <div className="text-center max-w-5xl mx-auto mb-4">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHO CAN USE THIS EXPERIENCE LETTER FORMAT?
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
                  Suitable for Different Employers and Employment
                </h2>
              </div>

            
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-3  rounded-2xl"
                  >
                  
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                 
                    <p className="font-roboto text-sm  font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section> */}
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Professional Promotion Letter Now
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                Create a professional promotion letter without editing templates
                or formatting documents. Add the required details, generate it
                instantly, and download a ready-to-use PDF
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Promotion Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
